export type SessionScalar = string | number | boolean | null | undefined;
export type FlatSessionPropertyBag = Readonly<Record<string, SessionScalar>>;

interface SessionLeaf {
    path: (string | number)[];
    value: Exclude<SessionScalar, undefined>;
}

interface FlattenOptions {
    preserveNull?: boolean;
    reservedNames?: readonly string[];
    canonicalPaths?: readonly (readonly string[])[];
}

function collectLeaves(
    value: unknown,
    path: (string | number)[],
    leaves: SessionLeaf[],
    preserveNull: boolean
): void {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        leaves.push({ path, value });
    } else if (preserveNull && value === null) {
        leaves.push({ path, value: null });
    } else if (Array.isArray(value)) {
        value.forEach((item, index) => collectLeaves(item, [...path, index], leaves, preserveNull));
    } else if (value !== null && typeof value === 'object') {
        for (const [key, item] of Object.entries(value)) {
            collectLeaves(item, [...path, key], leaves, preserveNull);
        }
    }
}

/**
 * Flattens browser JSON and typed session sections into indexed underscore paths.
 * Canonical paths take precedence; other collisions get suffixes without taking natural names.
 * Empty containers and undefined are omitted; preserveNull controls whether null leaves remain.
 */
export function flattenSessionPropertyBag(
    input: Readonly<Record<string, unknown>>,
    { preserveNull = false, reservedNames = [], canonicalPaths = [] }: FlattenOptions = {}
): Record<string, string | number | boolean | null> {
    const leaves: SessionLeaf[] = [];
    for (const [key, value] of Object.entries(input)) {
        collectLeaves(value, [key], leaves, preserveNull);
    }
    const values = new Map<string, string | number | boolean | null>();
    const canonical = new Map(canonicalPaths.map(path => [path.join('_'), JSON.stringify(path)]));
    const reserved = new Set([...reservedNames, ...canonical.keys()]);
    const originalNames = new Set(reserved);
    const nested: (SessionLeaf & { sortKey: string })[] = [];
    for (const leaf of leaves) {
        const key = leaf.path.join('_');
        reserved.add(key);
        if (canonical.has(key) && canonical.get(key) === JSON.stringify(leaf.path)) {
            values.set(key, leaf.value);
        } else if (leaf.path.length === 1 && !canonical.has(key)) {
            values.set(key, leaf.value);
            originalNames.add(key);
        } else {
            nested.push({ ...leaf, sortKey: JSON.stringify(leaf.path) });
        }
    }
    // JSON-encoded paths distinguish numeric array indexes from string object keys.
    nested.sort((left, right) => {
        const leftKey = left.sortKey;
        const rightKey = right.sortKey;
        return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
    });
    for (const leaf of nested) {
        const base = leaf.path.join('_');
        let key = base;
        if (originalNames.has(key) || values.has(key)) {
            let suffix = 2;
            key = `${base}_${suffix}`;
            while (reserved.has(key) || values.has(key)) {
                key = `${base}_${++suffix}`;
            }
        }
        values.set(key, leaf.value);
        reserved.add(key);
    }
    return Object.fromEntries(values);
}
